import type {
  PeriodType,
  WorkflowLimitRow,
  WorkspaceLimitRow,
} from '@/lib/api/generated/model';

import { parseLimitAmount } from './limitAmount';

/**
 * Whether saving starts the limit's period again at zero: an existing limit whose period
 * type changes (service spec 083 FR-011). Nothing has been spent in a period that begins
 * at the save, so such a save stops nothing, whatever its amount (spec 238).
 *
 * This supersedes 091 research R4, which held that a period-only change leaves the stop
 * count as it was. Spec 091's clarification still holds: a period change goes through the
 * same check as an amount change. The check now gives the right answer for it.
 *
 * `current` is `null` for a limit that does not exist yet. Creating one starts nothing
 * again, so its spend so far still counts.
 */
export function restartsPeriod(
  current: PeriodType | null,
  next: PeriodType,
): boolean {
  return current !== null && current !== next;
}

/**
 * How many workspaces a limit would stop, before it is saved (spec 073 FR-016).
 *
 * The requirement is disclosure, not prevention: an administrator halting spending
 * across the organization is a legitimate and often urgent action, and the console's
 * job is to make sure nobody does it by accident. So this answers "how many workspaces
 * will this stop" while the change is still a draft.
 *
 * Computed here rather than asked of the service. The service *does* report it — the
 * `LimitView` it returns carries `blocks_workspaces` — but it reports it for a limit
 * that has already been applied, which is one request too late to warn anybody. Every
 * figure this needs is already on the page: the table lists each workspace's spend for
 * the period and whether it carries an override. So the forecast is exact rather than
 * an estimate, and it costs no request.
 *
 * Compared as decimal strings via `Number`, not with the arithmetic the service uses.
 * That is safe *here* and nowhere else in this feature: this is a comparison whose
 * answer is a count shown to a human, not a figure that is stored, summed, or
 * reconciled against an invoice. A workspace within a floating-point hair of its limit
 * is one the administrator should be told about either way.
 *
 * The candidate goes through `parseLimitAmount` rather than through `Number` directly.
 * `Number('')` is `0`, and a limit of zero stops everything — so a bare numeric check
 * would answer a half-typed field with "this will stop all 40 workspaces", which is
 * both alarming and false. One definition of "is this a figure yet", shared with the
 * field that shows the reason it is not.
 */
export function workspacesStoppedBy(
  rows: readonly WorkspaceLimitRow[],
  candidate: string,
  { scope }: { scope: 'default' | 'workspace' },
): number {
  const parsed = parseLimitAmount(candidate);
  if (!parsed.ok) return 0;
  const limit = Number(parsed.amount);

  return rows.filter((row) => {
    // An overridden workspace does not follow the default, so lowering the default
    // cannot stop it. Counting it would overstate the damage and teach the
    // administrator to distrust the warning — which is worse than not showing one.
    if (scope === 'default' && row.is_override) return false;
    // A deleted workspace runs nothing, so no limit can stop it (spec 146).
    if (row.deleted) return false;
    // The spend the limit is judged by: today's for a Daily limit read in October, not
    // the month's (spec 237). An older service sends none, and the month's stands in.
    return Number(row.limit_consumed_amount ?? row.spend) >= limit;
  }).length;
}

/**
 * The same question for one workspace: would falling back to this default stop it now?
 *
 * Separate from the count above because the answer reads differently — "this stops the
 * workspace immediately" is a statement about the row in front of the reader, not a
 * tally across a table.
 *
 * Judged by what the default would count in its own period, which the service sends for
 * a workspace with an override (`default_consumed_amount`, spec 237): a Daily default
 * counts today, whatever period the override counted. An older service sends none, and
 * the period's spend stands in.
 */
export function wouldStop(
  row: Pick<WorkspaceLimitRow, 'spend' | 'default_consumed_amount'>,
  candidate: string,
): boolean {
  const parsed = parseLimitAmount(candidate);
  return (
    parsed.ok &&
    Number(row.default_consumed_amount ?? row.spend) >= Number(parsed.amount)
  );
}

/**
 * Would this cap stop this workflow now? (spec 080 FR-007.)
 *
 * The workflow tier's answer to `wouldStop` above, and separate from it because the rows
 * are different shapes — not because the arithmetic differs. `>=`, matching the service's
 * own `exhausted`: reaching a cap is the point dispatch stops, so a cap set exactly at
 * current spend stops the workflow immediately.
 *
 * Computed here rather than read from the mutation's `blocks_workflows`, for the reason
 * this file already gives about the default: the service reports that for a cap it has
 * *already applied*, which is one request too late to warn anyone. The workflow's spend
 * for the period is on the row in front of the reader: the spend in the cap's own period
 * (spec 237), which for a Daily cap read in October is today's, not the month's.
 */
export function workflowWouldStop(
  row: WorkflowLimitRow,
  candidate: string,
): boolean {
  const parsed = parseLimitAmount(candidate);
  return (
    parsed.ok && Number(row.limit_consumed_amount) >= Number(parsed.amount)
  );
}

/**
 * Would this cap be unable to fire, because the workspace's own limit is lower? (FR-007.)
 *
 * The two tiers stack — a dispatch must clear both — so a workflow cap at or above its
 * workspace's applied limit can never be the one that refuses a run. The service permits
 * it deliberately (a workspace limit lowered later would otherwise make an existing cap
 * retroactively illegal), so the console's job is to say so, not to refuse it.
 *
 * `false` when the workspace has no applied limit at all: there is no ceiling to be
 * above, and the workflow cap binds on its own — which is the whole point of this tier
 * stacking rather than replacing.
 *
 * Joined on `workspace_id`, never on position: the two tables are ordered by different
 * things, and the day one of them changes a positional join would report the wrong
 * workspace's limit without failing.
 */
export function exceedsWorkspaceLimit(
  row: WorkflowLimitRow,
  workspaces: readonly WorkspaceLimitRow[],
  candidate: string,
): boolean {
  return exceedsWorkspaceLimitFor(row.workspace_id, workspaces, candidate);
}

/**
 * The same question before the cap exists.
 *
 * Keyed on a workspace id rather than on a `WorkflowLimitRow`, because the add path has
 * no such row yet — it has a chosen workspace and a typed amount. Both callers share
 * this so the statement cannot appear on one path and not the other, which is exactly
 * what happened when only the change dialog had it.
 */
export function exceedsWorkspaceLimitFor(
  workspaceId: string,
  workspaces: readonly WorkspaceLimitRow[],
  candidate: string,
): boolean {
  const parsed = parseLimitAmount(candidate);
  if (!parsed.ok) return false;
  const workspace = workspaces.find((w) => w.workspace_id === workspaceId);
  if (!workspace?.limit) return false;
  return Number(parsed.amount) >= Number(workspace.limit);
}
